/**
 * Flow Design Prompt
 *
 * Guides users through designing a new Power Automate flow by gathering
 * requirements and suggesting appropriate patterns and connectors.
 */

import type { Prompt, PromptMessage } from "@modelcontextprotocol/sdk/types.js";
import { listTemplates, getCategories } from "../templates/index.js";

// ============================================================================
// Prompt Definition
// ============================================================================

export const flowDesignPrompt: Prompt = {
  name: "flow-design",
  description:
    "Design a new Power Automate flow. Guides you through requirements gathering, pattern selection, and provides a structured flow definition.",
  arguments: [
    {
      name: "goal",
      description: "What you want the flow to accomplish (e.g., 'Send approval emails when SharePoint items are created')",
      required: true,
    },
    {
      name: "triggers",
      description: "What should start the flow (e.g., 'SharePoint item created', 'scheduled daily', 'manual button')",
      required: false,
    },
    {
      name: "connectors",
      description: "Specific connectors to use (e.g., 'SharePoint, Outlook, Teams')",
      required: false,
    },
    {
      name: "complexity",
      description: "Desired complexity level: simple, moderate, or advanced",
      required: false,
    },
  ],
};

// ============================================================================
// Prompt Message Generation
// ============================================================================

export interface FlowDesignArgs {
  goal: string;
  triggers?: string;
  connectors?: string;
  complexity?: "simple" | "moderate" | "advanced";
}

export function generateFlowDesignMessages(args: FlowDesignArgs): PromptMessage[] {
  const templates = listTemplates();
  const categories = getCategories();

  const systemMessage = `You are a Power Automate flow design assistant. Your role is to help users design effective automation flows.

## Available Templates
${templates.map((t) => `- **${t.name}** (${t.id}): ${t.description}`).join("\n")}

## Template Categories
${categories.map((c) => `- ${c}`).join("\n")}

## Design Principles
1. Start with the simplest solution that meets requirements
2. Use built-in connectors when possible (they're more reliable)
3. Add error handling for critical paths
4. Consider retry logic for external API calls
5. Use scopes to group related actions
6. Add meaningful descriptions to actions
7. Use variables to reduce repeated expressions
8. Consider run history storage limits

## Common Patterns
- **Approval Flow**: Request > Wait for approval > Branch on outcome > Notify
- **Data Sync**: Trigger on change > Transform data > Upsert to target > Log results
- **Notification Fan-out**: Event > Get recipients > ForEach > Send notification
- **Error Handler**: Try scope > Catch scope (runAfter: Failed) > Log/Notify/Retry

## Expression Best Practices
- Use coalesce() for default values
- Use if() for conditional values
- Use concat() for string building
- Use formatDateTime() with ISO 8601 format
- Access trigger data with triggerBody(), triggerOutputs()
- Access action outputs with outputs('ActionName'), body('ActionName')

When designing a flow:
1. Clarify the goal and requirements
2. Identify the trigger type
3. List required connectors
4. Suggest a matching template or pattern
5. Provide a complete flow definition in JSON format
6. Explain each action and why it's needed
7. Highlight potential issues and improvements`;

  const userPrompt = buildUserPrompt(args);

  return [
    {
      role: "user",
      content: {
        type: "text",
        text: systemMessage,
      },
    },
    {
      role: "user",
      content: {
        type: "text",
        text: userPrompt,
      },
    },
  ];
}

function buildUserPrompt(args: FlowDesignArgs): string {
  const parts: string[] = [
    `## Flow Design Request`,
    ``,
    `**Goal:** ${args.goal}`,
  ];

  if (args.triggers) {
    parts.push(`**Trigger:** ${args.triggers}`);
  }

  if (args.connectors) {
    parts.push(`**Connectors:** ${args.connectors}`);
  }

  if (args.complexity) {
    parts.push(`**Complexity:** ${args.complexity}`);
  }

  parts.push(``);
  parts.push(`Please help me design this flow. Provide:`);
  parts.push(`1. A summary of the flow's purpose`);
  parts.push(`2. The recommended trigger and why`);
  parts.push(`3. Step-by-step actions needed`);
  parts.push(`4. Required connections`);
  parts.push(`5. Complete flow definition JSON that I can use with create_flow`);
  parts.push(`6. Any warnings or recommendations`);

  return parts.join("\n");
}

// ============================================================================
// Quick Design Templates
// ============================================================================

export interface QuickDesignTemplate {
  name: string;
  description: string;
  prompt: FlowDesignArgs;
}

export const quickDesignTemplates: QuickDesignTemplate[] = [
  {
    name: "Document Approval",
    description: "Approval workflow for documents uploaded to SharePoint",
    prompt: {
      goal: "When a document is uploaded to a SharePoint library, send it for approval to a manager. Notify the uploader of the outcome.",
      triggers: "SharePoint file created",
      connectors: "SharePoint, Approvals, Outlook",
      complexity: "moderate",
    },
  },
  {
    name: "Daily Report",
    description: "Scheduled flow to generate and email a daily report",
    prompt: {
      goal: "Every day at 8 AM, query data from a SharePoint list, generate a summary, and email it to stakeholders.",
      triggers: "Recurrence (daily at 8 AM)",
      connectors: "SharePoint, Outlook",
      complexity: "simple",
    },
  },
  {
    name: "Teams Alert",
    description: "Send Teams notifications when high-priority items are created",
    prompt: {
      goal: "When a high-priority item is created in a SharePoint list, post an alert to a Teams channel with item details and a link.",
      triggers: "SharePoint item created with condition",
      connectors: "SharePoint, Teams",
      complexity: "simple",
    },
  },
  {
    name: "Multi-System Sync",
    description: "Sync data between SharePoint and an external API",
    prompt: {
      goal: "When items change in SharePoint, sync them to an external REST API. Handle errors and track sync status.",
      triggers: "SharePoint item created or modified",
      connectors: "SharePoint, HTTP",
      complexity: "advanced",
    },
  },
];

/**
 * Get quick design templates for common scenarios
 */
export function getQuickDesignTemplates(): QuickDesignTemplate[] {
  return quickDesignTemplates;
}
